import {Inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {EmployeeShiftOverviewDto} from "../../company/_models/employee/employee-shift-overview-dto";

/**
 * Which employers a calendar link's events cover (#685). `account` is the merged feed over every active
 * employer of the signed-in account (a session carrying `ext_id`); `employee` is the legacy single-employer
 * link a password-only employee login still gets. The SPA never mints these itself — they are read verbatim
 * off `GetCalendarSubscription`/`ResetCalendarSubscription`.
 */
export type CalendarSubscriptionScope = 'account' | 'employee';

/**
 * The calendar subscription link the employee panel hands out. `path` is appended to
 * `${baseUrl}/ep/shift/calendar` to build the subscribable URL — its shape is unchanged from before #685, only
 * `scope` is additive.
 */
export interface CalendarSubscription {
  path: string;
  scope: CalendarSubscriptionScope;
}

@Injectable({
  providedIn: 'root'
})
export class ShiftHttpService {
  constructor(@Inject("BASE_URL") private baseUrl: string,
              private http: HttpClient) {
  }

  /**
   * ALL of the employee's assigned, worked shifts. The endpoint takes no range (#645): the server cross-loads every
   * event response anyway, so callers load once and filter by company calendar day locally
   * (`isInCompanyDayRange(shift.eventStart, …)`, see `_shared/time/company-day.ts`). The old start/end parameters
   * were browser-local midnights sent as instants, i.e. the bound depended on the viewer's zone.
   */
  getShiftOverview(): Observable<EmployeeShiftOverviewDto> {
    return this.http.get<EmployeeShiftOverviewDto>(`${this.baseUrl}/ep/shift/getshiftoverview`);
  }

  /**
   * The current calendar link, minted on first use and returned unchanged afterwards. See
   * {@link CalendarSubscription} — `scope` decides which helper text/behaviour the settings tile shows.
   */
  getCalendarSubscription(): Observable<CalendarSubscription> {
    return this.http.get<CalendarSubscription>(`${this.baseUrl}/ep/shift/getcalendarsubscription`);
  }

  /**
   * Replaces the current calendar link with a new one (#685). The OLD link stops working immediately (every
   * `Type=Calendar` row it and, for an account, its linked employees' legacy per-employee rows are deleted), so
   * every calendar app subscribed with the old link must be re-subscribed with the one this returns. No body.
   */
  resetCalendarSubscription(): Observable<CalendarSubscription> {
    return this.http.post<CalendarSubscription>(`${this.baseUrl}/ep/shift/resetcalendarsubscription`, null);
  }
}
